import { CarouselItemVariants } from 'constants/carousel';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Direction } from 'types/Utilities';
import Spinner from 'components/Qds/Spinner';
import Icon from 'components/ui/Icon';
import tailwind from 'utils/breakpoints';
import config from 'config';
import CarouselFilter from './CarouselFilter';
import BeforeAfter from './ItemVariants/BeforeAfter';
import Default from './ItemVariants/Default';
import type { Slide } from 'types/Utilities';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';

type Props = {
  counter?: boolean;
  getPhotos: (
    offset?: number,
    limit?: number,
    all?: boolean,
  ) => Promise<Record<string, { baphotos: APIBeforeAfterPhoto[] }>>;
  getPhotosAll: boolean;
  getPhotosLimit: number;
  isMobile?: boolean;
  itemVariant: 'default' | 'before_after';
  loadMorePhotos?: boolean;
  onClose?: () => void;
  onFilterChange: (option: Record<string, string>) => void;
  onImageClick: (idx: number) => void;
  onLoadMore: (slides: Slide[]) => void;
  slides: Slide[];
  totalCount?: string | number;
  variant: string;
};

const CarouselGrid = ({
  counter,
  getPhotos,
  getPhotosLimit,
  getPhotosAll = true,
  isMobile,
  loadMorePhotos = true,
  onClose,
  onFilterChange,
  onLoadMore,
  itemVariant,
  slides,
  totalCount,
  variant,
  ...props
}: Props): JSX.Element => {
  const [originalSlides, setOriginalSlides] = useState<Slide[]>(slides);
  const [carouselSlides, setCarouselSlides] = useState<Slide[]>(slides);
  const [isLoading, setIsLoading] = useState(true);
  const [, setHasAllImagesLoaded] = useState(false);
  const { beforeAndAfterPhotosGridContainerLimit } = config.defaults;

  useEffect(() => {
    if (itemVariant === CarouselItemVariants.BEFORE_AFTER && loadMorePhotos) {
      if (carouselSlides.length < beforeAndAfterPhotosGridContainerLimit) {
        void getPhotos(
          originalSlides.length,
          beforeAndAfterPhotosGridContainerLimit - slides.length,
          getPhotosAll,
        ).then((result) => {
          if (result.items) {
            const filteredPhotos = result.items.baphotos.filter(
              (photo) => photo.aftermedia && photo.beforemedia,
            );

            const updatePhotos = [...originalSlides, ...filteredPhotos];
            setOriginalSlides(updatePhotos);
            setCarouselSlides(updatePhotos);
            setIsLoading(false);
            onLoadMore && onLoadMore(updatePhotos);

            if (result.items.baphotos.length <= 1) {
              setHasAllImagesLoaded(true);
              setIsLoading(false);
            }
          } else {
            setIsLoading(false);
          }
        });
      }

      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, []);

  /**
   * Resolve the slider item component by variant
   *
   * @returns JSX.Element
   */
  const resolveComponent = (): JSX.Element => {
    switch (itemVariant) {
      case CarouselItemVariants.BEFORE_AFTER:
        return (
          <BeforeAfter
            slides={carouselSlides as unknown as APIBeforeAfterPhoto[]}
            isGrid
            variant={variant}
            {...props}
          />
        );
      default:
        return (
          <Default
            slides={carouselSlides}
            isGrid
            variant={variant}
            {...props}
          />
        );
    }
  };

  /**
   * Update the slides by the given treatment filter value
   */
  const updateResultsByTreatment = (option: Record<string, string>): void => {
    if (option.value === 'All') {
      return setCarouselSlides(originalSlides);
    }

    const filteredSlides = originalSlides.filter((slide) => {
      return (
        (slide as APIBeforeAfterPhoto).beforeafterphoto?.treatment ===
        option.value
      );
    });
    setCarouselSlides(filteredSlides);
    onFilterChange(option);
  };

  return (
    <>
      <div className="flex justify-between items-center md:grid grid-cols-3">
        <div
          className={clsx('fixed h-screen w-full z-9', !isLoading && 'hidden')}
          style={{ top: '50%', left: '50%' }}
        >
          <Spinner size={24} />
        </div>
        <div
          className="md:rounded-full md:flex justify-center items-center md:h-8 md:w-8 md:bg-bluishGrey"
          onClick={() => {
            onClose?.();
          }}
        >
          <Icon
            type="ArrowShort"
            direction={Direction.LEFT}
            color={tailwind.theme.backgroundColor.tertiary}
            className="w-8 h-8 cursor-pointer md:hidden"
          />
          <Icon
            type="Angle"
            direction={Direction.LEFT}
            color={tailwind.theme.backgroundColor.tertiary}
            className="cursor-pointer hidden md:block"
          />
        </div>
        {!isMobile && counter ? (
          <p className="text-navy text-center hidden md:block">
            <strong>{totalCount || slides.length} Photos</strong>
          </p>
        ) : null}
        {itemVariant === CarouselItemVariants.BEFORE_AFTER && (
          <CarouselFilter
            variant="light"
            slides={originalSlides}
            isGrid
            updateResultsByTreatment={updateResultsByTreatment}
          />
        )}
      </div>

      <div
        className="mx-auto md:w-8/12 lg:w-7/12 mt-4 md:my-12"
        style={{
          maxWidth:
            itemVariant === CarouselItemVariants.BEFORE_AFTER ? '' : '700px',
        }}
      >
        <div className={clsx('mb-8', isLoading && 'invisible')}>
          {resolveComponent()}
        </div>
      </div>
    </>
  );
};

export default CarouselGrid;
